import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { LogoWordmark } from "@/components/Logo";
import {
  LayoutDashboard, Users, UserPlus, CalendarClock, Sparkles, FileSpreadsheet,
  Database, TrendingUp, UsersRound, Download, LogOut, Bell, GraduationCap, Building2, QrCode, Link2,
} from "lucide-react";
import { ChangePasswordDialog } from "@/components/ChangePasswordDialog";

const rmLinks = [
  { to: "/app/dashboard", label: "Dashboard", icon: LayoutDashboard },
  { to: "/app/add-customer", label: "Add Customer", icon: UserPlus },
  { to: "/app/customers", label: "My Customers", icon: Users },
  { to: "/app/schools", label: "School Drive", icon: GraduationCap },
  { to: "/app/followups", label: "Follow-ups", icon: CalendarClock },
  { to: "/app/notifications", label: "Notifications", icon: Bell },
  { to: "/app/bulk-upload", label: "Bulk Upload", icon: FileSpreadsheet },
];

const adminLinks = [
  { to: "/app/dashboard", label: "Dashboard", icon: LayoutDashboard },
  { to: "/app/analytics", label: "Analytics", icon: TrendingUp },
  { to: "/app/customers", label: "Customer Database", icon: Database },
  { to: "/app/schools", label: "School Drive", icon: GraduationCap },
  { to: "/app/mela", label: "Loan Mela", icon: Sparkles },
  { to: "/app/edu-fair", label: "Education Fair", icon: QrCode },
  { to: "/app/reflinks", label: "Reference Links", icon: Link2 },
  { to: "/app/branch-managers", label: "Branch Managers", icon: Building2 },
  { to: "/app/rms", label: "Manage RMs", icon: UsersRound },
  { to: "/app/notifications", label: "Notifications", icon: Bell },
  { to: "/app/bulk-upload", label: "Bulk Upload", icon: FileSpreadsheet },
  { to: "/app/export", label: "Excel Export", icon: Download },
];

const bmLinks = [
  { to: "/app/dashboard", label: "Dashboard", icon: LayoutDashboard },
  { to: "/app/analytics", label: "Analytics", icon: TrendingUp },
  { to: "/app/customers", label: "Customer Database", icon: Database },
  { to: "/app/mela", label: "Loan Mela", icon: Sparkles },
  { to: "/app/rms", label: "Manage RMs", icon: UsersRound },
  { to: "/app/notifications", label: "Notifications", icon: Bell },
  { to: "/app/export", label: "Excel Export", icon: Download },
];

export default function Sidebar({ onNavigate }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const links = user.role === "admin" ? adminLinks : user.role === "branch_manager" ? bmLinks : rmLinks;

  const doLogout = async () => {
    onNavigate?.();
    await logout();
    navigate("/login");
  };

  return (
    <aside className="w-64 shrink-0 bg-[#0A192F] text-white flex flex-col h-full" data-testid="sidebar">
      <div className="px-6 py-5 border-b border-slate-700/60">
        <LogoWordmark light />
      </div>
      <nav className="flex-1 px-3 py-5 space-y-1 overflow-y-auto">
        {links.map((l) => (
          <NavLink
            key={l.to}
            to={l.to}
            onClick={() => onNavigate?.()}
            data-testid={`nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
            className={({ isActive }) =>
              `flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm transition-colors ${
                isActive
                  ? "bg-emerald-500/20 text-emerald-400 font-semibold border-r-4 border-emerald-500"
                  : "text-slate-300 hover:bg-white/10 hover:text-white"
              }`
            }
          >
            <l.icon size={18} />
            {l.label}
          </NavLink>
        ))}
      </nav>
      <div className="px-4 py-4 border-t border-slate-700/60">
        <div className="flex items-center gap-3 mb-3 px-2">
          <div className="w-9 h-9 rounded-full bg-emerald-500/20 flex items-center justify-center text-emerald-400 font-bold">
            {user.name?.[0]?.toUpperCase()}
          </div>
          <div className="leading-tight">
            <div className="text-sm font-semibold">{user.name}</div>
            <div className="text-xs text-slate-400 capitalize">{user.role === "rm" ? "Relationship Mgr" : user.role === "branch_manager" ? "Branch Manager" : "Admin"}</div>
          </div>
        </div>
        <ChangePasswordDialog />
        <button
          onClick={doLogout}
          data-testid="logout-button"
          className="w-full px-4 py-2.5 bg-red-500/10 hover:bg-red-500/20 text-red-400 rounded-lg font-medium flex items-center gap-2 transition-all border border-red-500/20"
        >
          <LogOut size={18} /> Log Out
        </button>
      </div>
    </aside>
  );
}
